/**
 * Datasource remoto ReminderSettings: abstração + implementação Firestore.
 * ReminderSettings é subcoleção de users: users/{userId}/reminder_settings/{id}
 * Recebe Firestore por injeção (clean architecture); se não passado, usa getFirestoreDb().
 *
 * Migração: se existir `message_test_sended` legado em `users/{uid}`, copia-se para
 * o documento de reminder_settings na leitura (ou sintético se ainda não houver doc).
 */

import type { DocumentSnapshot, Firestore, QuerySnapshot } from 'firebase/firestore';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  addDoc,
  collection,
  getDocs,
  limit,
  onSnapshot,
  query,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import {
  ReminderSettings,
  ReminderSettingsRef,
  REMINDER_OFFSETS_DAYS_DEFAULT,
  reminderSettingsSchema,
  UserRef,
} from '@/core/domain';

export interface ReminderSettingsRemoteDataSource {
  getById(userId: string): Promise<ReminderSettings | null>;
  create(userId: string, settings: ReminderSettings): Promise<ReminderSettings>;
  update(userId: string, id: string, settings: Partial<ReminderSettings>): Promise<ReminderSettings>;
  delete(userId: string, id: string): Promise<void>;
  /**
   * Escuta `users/{userId}` e o primeiro doc de `reminder_settings` (mesma
   * lógica de legado que `getById`) até `message_test_sended === true`.
   */
  subscribeMessageTestSendedFlag(
    userId: string,
    onNext: (sended: boolean) => void,
    onError?: (error: Error) => void
  ): () => void;
}

/**
 * Normaliza dados vindos do Firestore para o schema.
 */
function toReminderSettings(
  id: string,
  data: Record<string, unknown>,
  userId: string
): ReminderSettings {
  const now = new Date().toISOString();
  const rawFlag = data.message_test_sended;
  const message_test_sended =
    typeof rawFlag === 'boolean' ? rawFlag : false;
  const rawWamid = data.message_test_outbound_wamid;
  const message_test_outbound_wamid =
    typeof rawWamid === 'string' && rawWamid.trim().length > 0 ? rawWamid.trim() : null;
  const normalized = {
    id,
    user_id: (data.user_id as string | undefined) ?? userId,
    reminder_offsets_days: data.reminder_offsets_days,
    preferred_notification_time:
      (data.preferred_notification_time as string | null | undefined) ?? null,
    message_test_sended,
    message_test_outbound_wamid,
    created_at: (data.created_at as string | undefined) ?? now,
    updated_at: (data.updated_at as string | undefined) ?? now,
  };
  const parsed = reminderSettingsSchema.safeParse(normalized);
  if (!parsed.success) throw new Error('Invalid reminder settings from Firestore');
  return parsed.data;
}

export class ReminderSettingsFirestoreDataSource implements ReminderSettingsRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  private settingsDocRef(userId: string, id: string) {
    return doc(this.getDb(), UserRef.collection, userId, ReminderSettingsRef.collection, id);
  }

  private settingsCollectionRef(userId: string) {
    return collection(this.getDb(), UserRef.collection, userId, ReminderSettingsRef.collection);
  }

  async getById(userId: string): Promise<ReminderSettings | null> {
    const db = this.getDb();
    const userDocRef = doc(db, UserRef.collection, userId);
    const [settingsSnap, userSnap] = await Promise.all([
      getDocs(this.settingsCollectionRef(userId)),
      getDoc(userDocRef),
    ]);

    const legacyUserTest =
      userSnap.exists() && userSnap.data()?.message_test_sended === true;

    if (!settingsSnap.empty) {
      const d = settingsSnap.docs[0];
      let s = toReminderSettings(d.id, d.data() as Record<string, unknown>, userId);
      if (legacyUserTest && !s.message_test_sended) {
        const now = new Date().toISOString();
        await setDoc(d.ref, { message_test_sended: true, updated_at: now }, { merge: true });
        s = { ...s, message_test_sended: true, updated_at: now };
      }
      return s;
    }

    if (legacyUserTest) {
      const uData = userSnap.data();
      const now = new Date().toISOString();
      const created =
        typeof uData?.created_at === 'string' ? uData.created_at : now;
      const synthetic: ReminderSettings = {
        id: userId,
        user_id: userId,
        reminder_offsets_days: [...REMINDER_OFFSETS_DAYS_DEFAULT],
        preferred_notification_time: '09:00',
        message_test_sended: true,
        message_test_outbound_wamid: null,
        created_at: created,
        updated_at: now,
      };
      return reminderSettingsSchema.parse(synthetic);
    }

    return null;
  }

  async create(userId: string, settings: ReminderSettings): Promise<ReminderSettings> {
    const ref = this.settingsCollectionRef(userId);
    const docRef = await addDoc(ref, settings);
    const id = docRef.id;
    const created = { ...settings, id };
    return toReminderSettings(id, created as unknown as Record<string, unknown>, userId);
  }

  async update(userId: string, id: string, settings: Partial<ReminderSettings>): Promise<ReminderSettings> {
    const ref = this.settingsDocRef(userId, id);
    const now = new Date().toISOString();
    const payload: Record<string, unknown> = { ...settings, updated_at: now };
    await setDoc(ref, payload, { merge: true });
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Reminder settings not found after update');
    return toReminderSettings(snap.id, snap.data() as Record<string, unknown>, userId);
  }

  async delete(userId: string, id: string): Promise<void> {
    const ref = this.settingsDocRef(userId, id);
    await deleteDoc(ref);
  }

  subscribeMessageTestSendedFlag(
    userId: string,
    onNext: (sended: boolean) => void,
    onError?: (error: Error) => void
  ): () => void {
    const db = this.getDb();
    const userRef = doc(db, UserRef.collection, userId);
    const settingsQ = query(this.settingsCollectionRef(userId), limit(1));

    let latestUser: DocumentSnapshot | null = null;
    let latestSettings: QuerySnapshot | null = null;

    const emit = (): void => {
      const legacy =
        latestUser?.exists() === true &&
        latestUser.data()?.message_test_sended === true;
      let fromDoc = false;
      if (latestSettings != null && !latestSettings.empty) {
        const d = latestSettings.docs[0];
        const raw = d.data()?.message_test_sended;
        fromDoc = raw === true;
      }
      onNext(legacy || fromDoc);
    };

    const unsubUser = onSnapshot(
      userRef,
      (snap) => {
        latestUser = snap;
        emit();
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    );

    const unsubSettings = onSnapshot(
      settingsQ,
      (snap) => {
        latestSettings = snap;
        emit();
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    );

    return () => {
      unsubUser();
      unsubSettings();
    };
  }
}
